import { useEffect, useRef, useCallback, useState } from 'react';

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
  'pointerdown',
];

/**
 * Two-phase idle timeout:
 *   Phase 1 – after `idleMs` of inactivity, shows a warning (returned as state).
 *   Phase 2 – after `warningMs` more, fires `onIdle` (logout).
 *
 * Returns { showWarning, secondsLeft, dismissWarning }.
 * Calling `dismissWarning` (or any user activity while the warning is NOT shown)
 * resets everything back to Phase 1.
 */
export function useIdleTimeout({
  onIdle,
  idleMs = 15 * 60 * 1000,
  warningMs = 30 * 1000,
  enabled = true,
}) {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warningMs / 1000));

  const idleTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const onIdleRef = useRef(onIdle);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    idleTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const startCountdown = useCallback(() => {
    const totalSecs = Math.ceil(warningMs / 1000);
    setSecondsLeft(totalSecs);
    setShowWarning(true);

    let remaining = totalSecs;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearAllTimers();
        setShowWarning(false);
        onIdleRef.current?.();
      }
    }, 1000);
  }, [warningMs, clearAllTimers]);

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    idleTimerRef.current = setTimeout(() => startCountdown(), idleMs);
  }, [idleMs, clearAllTimers, startCountdown]);

  const dismissWarning = useCallback(() => {
    startIdleTimer();
  }, [startIdleTimer]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    startIdleTimer();

    const handler = () => {
      if (!countdownRef.current) {
        startIdleTimer();
      }
    };

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handler, { passive: true }),
    );

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handler),
      );
    };
  }, [enabled, startIdleTimer, clearAllTimers]);

  return { showWarning, secondsLeft, dismissWarning };
}
